import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router} from '@angular/router';
import {Serializer, slk, surveyLocalization, SurveyModel} from "survey-core";
import {environment} from "../../environments/environment";
import {RuntimeLoadModuleTrigger} from "../util/runtime-load-module.trigger";
import {firstValueFrom, Subject} from "rxjs";
import {BirthDatePickerQuestion, BodyMapQuestion, QuestionNames} from "@allaihealth/angular-survey";
import {CommonModulesResponse} from "dci-service-shared";
import {HttpClient} from "@angular/common/http";
import {AllaiSurveyModel} from "../util/allai-survey-model";

/** Service that handles global interactions with SurveyJS and survey routing */
@Injectable({
	providedIn: 'root'
})
export class SurveyService {
	/** {@link localStorage} storage key for language preference */
	static readonly localStorageLanguageKey:string = 'survey-locale';

	/** Current patient survey loaded */
	private currentSurveyId:string;

	/** Subject for loading/unloading modules */
	private onLoadUnload:Subject<RuntimeLoadModuleTrigger> = new Subject();

	/** Loaded common modules from the survey */
	private _commonModules:CommonModulesResponse;

	/** The current survey model that is loaded */
	private currentSurvey:SurveyModel;

	/** All survey's current locale */
	private _locale:string = environment.defaultLanguage;

	constructor(private router:Router, private http:HttpClient) {
	}

	init() {
		this.setSurveyJSLicense();

		surveyLocalization.supportedLocales = environment.enabledLanguages;

		this.setDefaultLanguage();

		// add the load module on condition trigger
		Serializer.addClass(
			RuntimeLoadModuleTrigger.triggerName,
			["moduleId:moduleId", "sourceId:sourceId"],
			() => new RuntimeLoadModuleTrigger(this.onLoadUnload),
			"surveytrigger"
		);

		BodyMapQuestion.registerQuestion();
		BirthDatePickerQuestion.registerQuestion();
		QuestionNames.addFields();
	}

	/** Set the survey's default language */
	setDefaultLanguage():void {
		const enabledLanguages = new Set<string>(environment.enabledLanguages);

		// check if the language has been set previously in local storage
		const localStorageLanguage = localStorage.getItem(SurveyService.localStorageLanguageKey);

		// if the local storage language is an available language in the survey
		if(localStorageLanguage && enabledLanguages.has(localStorageLanguage)) {
			console.info(`Using local storage language: ${localStorageLanguage}`);
			this.locale = localStorageLanguage;

			// else, try to use the one set in the browser
		} else if(navigator.language) {
			if(enabledLanguages.has(navigator.language)) {
				console.info(`Using navigator language: ${navigator.language}`);
				surveyLocalization.defaultLocale = navigator.language;
				this.locale = navigator.language;
			} else {
				const langCode = navigator.language.split('-')[0];
				if(enabledLanguages.has(langCode)) {
					console.info(`Using navigator language: ${langCode}`);
					surveyLocalization.defaultLocale = langCode;
					this.locale = langCode;
				}
			}
		}
	}

	/** Load all common survey models (error, auth, etc) */
	private async canActivateCommonModules() {
		if(!this._commonModules) {
			this._commonModules = await firstValueFrom(this.http.get<CommonModulesResponse>(`${environment.apiUrl}/module/common`));
		}
	}

	/** Redirect the user to the generic error page */
	redirectToErrorPage() {
		this.router.navigate(['/error', 'unknown']).then();
	}

	/** Set the survey's license key */
	private setSurveyJSLicense() {
		slk(`${environment.surveyJSLicense}`);
	}

	/** Instantiate a new survey which the survey service will monitor */
	newSurvey() {
		this.currentSurvey = new AllaiSurveyModel();

		if(this._locale) {
			this.currentSurvey.locale = this._locale;
			this.currentSurvey.setVariable("locale", this._locale);
		}

		return this.currentSurvey;
	}

	/** All survey's current locale */
	set locale(value:string) {
		this._locale = value;

		localStorage.setItem(SurveyService.localStorageLanguageKey, value);

		if(this.currentSurvey) {
			this.currentSurvey.locale = value;
			this.currentSurvey.setVariable("locale", value);
		}
	}

	get locale():string {
		return this._locale;
	}

	/** Current patient survey loaded */
	get surveyId() {
		return this.currentSurveyId;
	}

	/** Subject for loading/unloading modules */
	get onUnloadLoadModule() {
		return this.onLoadUnload.asObservable();
	}

	/** Loaded common modules from the survey */
	get commonModules():CommonModulesResponse {
		return this._commonModules;
	}

	/** Route guard for the survey */
	static canActivate(route:ActivatedRouteSnapshot) {
		const service = inject(SurveyService);
		service.currentSurveyId = route.paramMap.get("surveyId");
		return typeof service.currentSurveyId == "string";
	}

	/** Load all common survey models (error, auth, etc) */
	static canActivateCommonModules() {
		return inject(SurveyService).canActivateCommonModules();
	}
}
